import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'react-bootstrap';

import * as ReactSpark from "react-spark";
import forms from '../../components/forms';
import { Page } from '../page/Page';
import { actionCreator, stateSelectors } from './redux';
import { fetchClientData } from './redux/client-action-creator';
import CommunicationConsent from '../../components/communication-consent/CommunicationConsent';
import config from '../../../config';
import services from '../../../services';

const { Type, Stack } = ReactSpark;

const { UserService, DeviceService } = services;

const { Email, PhoneNumber, SocialSecurity, EmploymentStatus, Disclosures, ContinueButton } = forms.configured;
const { Form } = forms;

const LEAD_SOURCE_INCLUDE_SSN_INPUT = ['lendingTree', 'quinstreet', 'credible'];
const LEAD_SOURCE_NOT_INCLUDE_PHONE = ['lendingTree', 'quinstreet'];
const LEAD_SOURCE_NOT_INCLUDE_EMAIL = ['lendingTree', 'quinstreet'];
const LEAD_SOURCE_NOT_INCLUDE_EMPLOYMENT_STATUS = ['nerdwallet', 'supermoney'];
const VALID_EMPLOYMENT_STATUSES = Object.values(config.global.employmentStatuses);

export class LeadCompletePageComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            formValues: {
                email: this.props.email,
                employmentStatus: this.props.employmentStatus
            },
            clientResidentState: this.props.clientResidentState
        };
    }

    componentDidMount() {
        DeviceService.setBlackBoxId();

        // Check if we already have client data from UserService
        const userFirstName = UserService.get('firstName');

        // If we don't have client data from UserService, fetch it from API
        if (!userFirstName && this.props.fetchClientData) {
            this.props.fetchClientData();
        }
    }

    onSubmit = (e, formState) => {
        const formValues = formState.getValues();
        const parsedForm = this.parseForm(formValues);
        return this.props.submit(parsedForm)
            .then(response => {
                // Call success callback if provided
                if (this.props.onSubmitSuccess) {
                    this.props.onSubmitSuccess(response);
                }
                return response;
            })
            .catch(error => {
                // Call error callback if provided
                if (this.props.onSubmitError) {
                    this.props.onSubmitError(error);
                }
                throw error;
            });
    };

    parseForm = (formValues) => {
        const parsedForm = formValues;
        const { phone, socialSecurity, disclosures } = formValues;
        parsedForm.employmentStatus = formValues.employmentStatus
            ? formValues.employmentStatus : this.props.employmentStatus;
        formValues.phone
            ? parsedForm.phone = PhoneNumber.cleanValue(phone) : '';
        formValues.email
            ? parsedForm.email = formValues.email : '';
        formValues.socialSecurity
            ? parsedForm.socialSecurity = socialSecurity.replace(/-/g, '')
            : '';
        parsedForm.disclosures = disclosures[0] === 'disclosures';
        parsedForm.leadSource = this.props.leadSource;
        parsedForm.blackboxId = window.localStorage.getItem('blackboxId') || ';';
        parsedForm.deviceFingerprint = window.localStorage.getItem('blackboxId') || ';';
        return parsedForm;
    };

    onChange = (elementKey, elementData, { getValues, reset }) => {
        this.setState({
            formValues: getValues(),
            reset
        });
    };

    render() {
        const { firstName, leadSource, isFetchingLeadComplete, hasValidEmploymentStatus } = this.props;
        const { email, employmentStatus } = this.state.formValues;

        const shouldRenderEmploymentStatusField = () => {
            if (!LEAD_SOURCE_NOT_INCLUDE_EMPLOYMENT_STATUS.includes(leadSource)) return true;
            if (!hasValidEmploymentStatus) return true;
            return false;
        };

        return (
            <Page
                id="leadCompletePage"
                loading={this.props.fetching}
                size={8}
                heading="Account Details"
            >
                <Stack className="sprk-u-pbl">
                    <Stack.Item>
                        <Type.BodyTwo>
                            {firstName}, we just need a few more things from you to continue your application.
                        </Type.BodyTwo>
                    </Stack.Item>
                </Stack>
                <Form
                    id="leadCompleteForm"
                    name="leadComplete"
                    onChange={this.onChange}
                    onSubmit={this.onSubmit}
                >
                    {shouldRenderEmploymentStatusField()
                    && <Row>
                        <Col md={8} className="form-group">
                            <EmploymentStatus value={employmentStatus} />
                        </Col>
                    </Row>
                    }
                    {!LEAD_SOURCE_NOT_INCLUDE_PHONE.includes(leadSource)
                    && <Row>
                        <Col md={12} className="form-group">
                            <PhoneNumber />
                        </Col>
                    </Row>
                    }
                    {LEAD_SOURCE_INCLUDE_SSN_INPUT.includes(leadSource)
                    && <Row>
                        <Col md={12} className="form-group">
                            <SocialSecurity />
                        </Col>
                    </Row>
                    }
                    {!LEAD_SOURCE_NOT_INCLUDE_EMAIL.includes(leadSource)
                    && <Row>
                        <Col md={12} className="form-group">
                            <Email value={email} />
                        </Col>
                    </Row>
                    }
                    <Row>
                        <Col md={12} className="form-group">
                            <Disclosures type="accountCreation" clientResidentState={this.state.clientResidentState}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} className="form-group">
                            <ContinueButton
                                id="continueButton"
                                text="Continue"
                                loading={isFetchingLeadComplete}
                            />
                        </Col>
                    </Row>
                </Form>
                <CommunicationConsent />
            </Page>
        );
    }
}

LeadCompletePageComponent.propTypes = {
    firstName: PropTypes.string.isRequired,
    leadSource: PropTypes.string.isRequired,
    isFetchingLeadComplete: PropTypes.bool.isRequired,
    onSubmitSuccess: PropTypes.func,
    onSubmitError: PropTypes.func
};

const isValidEmploymentStatus = () => {
    const status = UserService.get('employmentStatus');
    if (!status) {
        return false;
    }
    return VALID_EMPLOYMENT_STATUSES.includes(status);
};

const mapStateToProps = (state) => {
    // First try UserService (for application-ui)
    const userFirstName = UserService.get('firstName');
    const userEmail = UserService.get('email');
    const userEmploymentStatus = UserService.get('employmentStatus');
    const userLeadSource = UserService.get('leadSource');
    const userState = UserService.get('state');

    // Then check Redux state from our new action (for web-ui)
    const clientData = state.client && state.client.clientJson;
    const personalInfo = clientData && clientData.personalInformation || {};
    const employmentInfo = clientData && clientData.employmentInformation || {};
    const loanLeadSource = state.loan && state.loan.loanJson && state.loan.loanJson.leadSource;

    return {
        firstName: userFirstName || personalInfo.firstName || '',
        email: userEmail || personalInfo.email || '',
        employmentStatus: userEmploymentStatus || employmentInfo.employmentStatus || '',
        leadSource: userLeadSource || clientData.leadSource || loanLeadSource || '',
        hasValidEmploymentStatus: isValidEmploymentStatus(),
        clientResidentState: userState || personalInfo.state || '',
        isFetchingLeadComplete: stateSelectors.isFetchingLeadComplete(state),
        code: stateSelectors.getCode(state),
    };
};

const mapDispatchToProps = (dispatch) => ({
    submit: (formData) => dispatch(actionCreator.submitLeadComplete(formData)),
    fetchClientData: () => dispatch(fetchClientData())
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LeadCompletePageComponent);
